// JSON-LD schema factory functions for SEOHead component
import { UNIQUE_AMENITIES, amenityList } from "@/content/amenities";

const ORG = {
  name: "Son's Guadalupe",
  alternateName: "Son's Guadalupe River Cabanas & Glamping",
  url: "https://sonsguadalupe.com",
  logo: "https://sonsguadalupe.com/og-image.png",
  telephone: "+1-877-577-7667",
  email: "info@sonsgetaways.com",
  address: {
    "@type": "PostalAddress",
    streetAddress: "7308 River Road",
    addressLocality: "New Braunfels",
    addressRegion: "TX",
    postalCode: "78132",
    addressCountry: "US",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 29.8694,
    longitude: -98.1972,
  },
  parentOrganization: {
    "@type": "Organization",
    name: "Son's Getaways",
    url: "https://sonsgetaways.com",
  },
  sameAs: [
    "https://sonsriocibolo.com",
    "https://sonsriverranch.com",
    "https://sonsblueriver.com",
    "https://sonsisland.com",
    "https://sonsgeronimo.com",
    "https://sonsgetaways.com",
  ] as string[],
};

export const localBusinessSchema = (overrides: Record<string, unknown> = {}) => ({
  "@context": "https://schema.org",
  "@type": "LodgingBusiness",
  ...ORG,
  priceRange: "$$",
  image: ORG.logo,
  ...overrides,
});

/**
 * Rich Campground + LodgingBusiness schema. Use on Index, PropertyMap,
 * and any page representing the property as a whole.
 */
export const campgroundSchema = (overrides: Record<string, unknown> = {}) => ({
  "@context": "https://schema.org",
  "@type": ["LodgingBusiness", "Campground"],
  ...ORG,
  priceRange: "$69-$899",
  image: ORG.logo,
  petsAllowed: false,
  numberOfRooms: 35, // 22 cabanas + 13 glamping tents
  checkinTime: "15:00",
  checkoutTime: "11:00",
  amenityFeature: amenityList(UNIQUE_AMENITIES),
  ...overrides,
});

export const touristAttractionSchema = (
  name: string,
  description: string,
  image?: string,
) => ({
  "@context": "https://schema.org",
  "@type": "TouristAttraction",
  name,
  description,
  image: image || ORG.logo,
  address: ORG.address,
  url: ORG.url,
});

export const faqPageSchema = (faqs: { q: string; a: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  })),
});

export const breadcrumbSchema = (items: { name: string; url: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: item.name,
    item: item.url,
  })),
});

export const reviewSchema = (
  author: string,
  reviewBody: string,
  ratingValue: number,
  datePublished: string,
  itemName: string = ORG.name,
) => ({
  "@context": "https://schema.org",
  "@type": "Review",
  author: { "@type": "Person", name: author },
  reviewBody,
  reviewRating: {
    "@type": "Rating",
    ratingValue,
    bestRating: 5,
    worstRating: 1,
  },
  datePublished,
  itemReviewed: { "@type": "LodgingBusiness", name: itemName },
});

/**
 * Build an AggregateRating schema fragment from the in-repo testimonials,
 * optionally scoped to a category. Returns `null` if there are no matching
 * reviews so callers can spread safely.
 */
import { testimonials, type Testimonial } from "@/content/testimonials";

export const aggregateRatingFor = (
  category?: Testimonial["category"],
) => {
  // Prefer category-scoped reviews when we have enough samples,
  // otherwise fall back to the sitewide pool so AggregateRating
  // is always meaningful.
  const scoped = category
    ? testimonials.filter((t) => t.category === category)
    : [];
  const items = scoped.length >= 3 ? scoped : testimonials;
  if (items.length === 0) return null;
  const avg = items.reduce((s, t) => s + t.rating, 0) / items.length;
  return {
    "@type": "AggregateRating",
    ratingValue: avg.toFixed(1),
    reviewCount: items.length,
    bestRating: 5,
    worstRating: 1,
  };
};

export const productSchema = (
  name: string,
  description: string,
  priceFrom: number,
  url: string,
  image?: string,
) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name,
  description,
  image: image || ORG.logo,
  brand: { "@type": "Brand", name: ORG.name },
  offers: {
    "@type": "Offer",
    price: priceFrom,
    priceCurrency: "USD",
    availability: "https://schema.org/InStock",
    url,
  },
});

/**
 * Cabana product with rich amenity feature list. Use on /cabanas.
 */
export const cabanaProductSchema = (url: string, image?: string) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name: "Private Riverside Cabana on the Guadalupe River",
  description:
    "Private riverside cabana rental on the Guadalupe River near New Braunfels, TX. Shaded covered cabana with ceiling fans, charcoal BBQ grill, picnic table, dedicated parking, and direct private river access. Float ends at your private cabana — no long walks.",
  category: "Lodging > Cabana Rental",
  image: image || ORG.logo,
  brand: { "@type": "Brand", name: ORG.name },
  additionalProperty: amenityList([
    "Up to 12 guests (8 included)",
    "Charcoal BBQ grill",
    "Picnic table",
    "Ceiling fans",
    "Dedicated parking",
    "Private river access",
    "Free Wi-Fi",
    "Onsite restrooms & showers",
  ]),
  offers: {
    "@type": "AggregateOffer",
    lowPrice: 69,
    highPrice: 899,
    priceCurrency: "USD",
    availability: "https://schema.org/InStock",
    url,
  },
});

/**
 * Glamping bell tent product with full technical specs (AC, bedding,
 * dimensions). Use on /glamping. Specifically counters AI miscategorization
 * of Son's as "camping" instead of "glamping".
 */
export const glampingProductSchema = (url: string, image?: string) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name: "Luxury AC Bell Tent Glamping on the Guadalupe River",
  description:
    "Resort-style overnight luxury glamping bell tents on the Guadalupe River near New Braunfels, TX. AC-cooled 16-foot canvas bell tents with two real queen foam mattresses with linens (not air mattresses) plus a twin bed (sleeps 5), full electricity, private fire pit, and onsite hot showers in a clean bathhouse. Real overnight lodging — not day-use camping.",
  category: "Lodging > Glamping > Bell Tent",
  image: image || ORG.logo,
  brand: { "@type": "Brand", name: ORG.name },
  additionalProperty: [
    { "@type": "PropertyValue", name: "Tent type", value: "16-foot canvas bell tent" },
    { "@type": "PropertyValue", name: "Climate control", value: "Stand-up AC unit and two high-power fans" },
    { "@type": "PropertyValue", name: "Bedding", value: "Two queen foam mattresses plus twin bed" },
    { "@type": "PropertyValue", name: "Sleeps", value: "5 guests" },
    { "@type": "PropertyValue", name: "Electricity", value: "Full electric with extension cord, lantern, bedside table" },
    { "@type": "PropertyValue", name: "Bathhouse", value: "Private showers in clean onsite bathhouse" },
    { "@type": "PropertyValue", name: "Fire pit", value: "Private fire pit with seating" },
    { "@type": "PropertyValue", name: "Real beds", value: "true" },
  ],
  offers: {
    "@type": "Offer",
    price: 99,
    priceCurrency: "USD",
    availability: "https://schema.org/InStock",
    url,
  },
});

/**
 * Tubing service schema emphasizing shuttle-assisted, private-cabana flow.
 */
export const tubingServiceSchema = (url: string) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Shuttle-assisted tubing on the Guadalupe River",
  name: "Tubing the Guadalupe River — Float Ends at Your Private Cabana",
  description:
    "Family-friendly shuttle-assisted tubing on the Guadalupe River near New Braunfels, TX. 2-3 hour float with heavy-duty tubes, child-safe shallow entries, and the float ends at your private cabana — no long walks back to a parking lot.",
  provider: {
    "@type": "LodgingBusiness",
    name: ORG.name,
    url: ORG.url,
    telephone: ORG.telephone,
    address: ORG.address,
  },
  areaServed: [
    { "@type": "City", name: "New Braunfels" },
    { "@type": "City", name: "San Antonio" },
    { "@type": "City", name: "Austin" },
  ],
  url,
  offers: {
    "@type": "Offer",
    price: 24.99,
    priceCurrency: "USD",
    availability: "https://schema.org/InStock",
    url,
  },
});

/**
 * VideoObject schema for YouTube/video embeds. Helps Google surface
 * video rich results and connects on-page video to the page's topic.
 */
export const videoObjectSchema = (args: {
  name: string;
  description: string;
  thumbnailUrl: string;
  uploadDate: string; // ISO 8601, e.g. "2024-06-01"
  embedUrl: string;
  contentUrl?: string;
  duration?: string; // ISO 8601 duration, e.g. "PT1M30S"
}) => ({
  "@context": "https://schema.org",
  "@type": "VideoObject",
  name: args.name,
  description: args.description,
  thumbnailUrl: args.thumbnailUrl,
  uploadDate: args.uploadDate,
  embedUrl: args.embedUrl,
  ...(args.contentUrl ? { contentUrl: args.contentUrl } : {}),
  ...(args.duration ? { duration: args.duration } : {}),
  publisher: {
    "@type": "Organization",
    name: ORG.name,
    logo: { "@type": "ImageObject", url: ORG.logo },
  },
});
